import { SVGIcon } from '@lyra/core/components/Icon'
import BabylonIcon from '@lyra/core/components/Icon/BabylonIcon'
import CubeIcon from '@lyra/core/components/Icon/CubeIcon'
import DRVToken from '@lyra/core/components/Icon/DRVToken'
import EigenLayerIcon from '@lyra/core/components/Icon/EigenLayerIcon'
import EnaIcon from '@lyra/core/components/Icon/EnaIcon'
import EtherFiIcon from '@lyra/core/components/Icon/EtherFiIcon'
import KelpIcon from '@lyra/core/components/Icon/KelpIcon'
import LBTC from '@lyra/core/components/Icon/LBTC'
import WstEthIcon from '@lyra/core/components/Icon/WstEthIcon'

import {
  ETHERFI_URL,
  HELP_BULL_RISKS_URL,
  HELP_BULL_STRATEGY_URL,
  HELP_COVERED_CALL_RISKS_URL,
  HELP_COVERED_CALL_SPREAD_RISKS_URL,
  HELP_COVERED_CALL_SPREAD_STRATEGY_URL,
  HELP_COVERED_CALL_STRATEGY_URL,
  HELP_COVERED_PUT_SPREAD_RISKS_URL,
  HELP_COVERED_PUT_SPREAD_STRATEGY_URL,
  KELP_URL,
  LOMBARD_URL,
  SWELL_URL,
} from './urls'
import {
  LBTCBConfig,
  LBTCCSConfig,
  LBTCPSConfig,
  rsETHCConfig,
  rswETHCConfig,
  sUSDeBULLConfig,
  weETHBConfig,
  weETHBULLConfig,
  weETHCConfig,
  weETHCSConfig,
  YieldTokenId,
} from './yield'

export type YieldStackLayer = {
  title: string
  icon: SVGIcon
  iconColor?: string
  description: string
  yieldValue?: string
  isBase?: boolean
  isOptions?: boolean
  isActive: boolean
}

export type YieldTokenValueProp = {
  label: string
  icon: SVGIcon
  iconColor?: string
}

export type YieldTokenTag = {
  label: string
  tooltip?: string
  icon?: SVGIcon
}

export type YieldTokenCopy = {
  id: YieldTokenId
  name: string
  subtitle: string
  howItWorksDocHref: string
  risksDocHref: string
  pointsLabel?: string
  pointsName?: string
  assetPath: string
  backgroundColor: string
  borderColor: string
  yieldStack: YieldStackLayer[]
  collateralUrl: string
  waitingTime?: {
    label: string
    tooltip?: string
  }
}

const SUSDE_COLOR = '#E4E7EB'
const RSETH_COLOR = '#0C4338'
const RSWETH_COLOR = '#2F42EC'
const WEETH_COLOR = '#8e4df0'
const LBTC_COLOR = '#154d4d'

const WEEKLY_WAITING_TIME = {
  label: 'Every Friday',
  tooltip: 'Withdraws are processed at the end of the epoch, approximately every 7 days',
}

export const rswETHCCopy: YieldTokenCopy = {
  id: YieldTokenId.RSWETHC,
  name: 'rswETH Harvest',
  subtitle: 'Grow your rswETH when the price of ETH trends sideways',
  howItWorksDocHref: HELP_COVERED_CALL_STRATEGY_URL,
  risksDocHref: HELP_COVERED_CALL_RISKS_URL,
  backgroundColor: `${RSWETH_COLOR}1A`,
  borderColor: '#2F42EC',
  assetPath: '/images/yield/rswETHC.png',
  collateralUrl: SWELL_URL,
  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy selling out-of-the-money call options on rswETH to generate USDC yield. Yields often vary between 10-50% p.a.',
      isOptions: true,
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your rswETH into Derive.',
      yieldValue: `${rswETHCConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'EigenLayer Points',
      icon: <EigenLayerIcon />,
      description: 'Earn EigenLayer points for restaking to secure the AVS network.',
      yieldValue: 'Variable',
      isActive: true,
    },
    {
      title: 'ETH Staking Rewards',
      icon: <WstEthIcon />,
      iconColor: '#00A3FF',
      description: 'Earn ETH staking rewards for securing the Ethereum network.',
      isBase: true,
      isActive: true,
    },
  ],
}

export const weETHCCopy: YieldTokenCopy = {
  id: YieldTokenId.WEETHC,
  name: 'weETH Harvest',
  subtitle: 'Grow your weETH when the price of ETH trends sideways',
  howItWorksDocHref: HELP_COVERED_CALL_STRATEGY_URL,
  risksDocHref: HELP_COVERED_CALL_RISKS_URL,
  pointsLabel: 'EtherFi Points (4x)',
  pointsName: 'Points',
  backgroundColor: `${WEETH_COLOR}12`,
  borderColor: WEETH_COLOR,
  assetPath: '/images/yield/weETHC.png',
  collateralUrl: ETHERFI_URL,
  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy selling out-of-the-money call options on weETH to generate USDC yield. Yields often vary between 10-50% p.a.',
      isOptions: true,
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your weETH into Derive.',
      yieldValue: `${weETHCConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'EtherFi Points',
      icon: <EtherFiIcon />,
      description:
        'Earn boosted EtherFi Points for choosing EtherFi as your liquid restaking provider.',
      yieldValue: '4x Multiplier',
      isActive: true,
    },
    {
      title: 'EigenLayer Points',
      icon: <EigenLayerIcon />,
      description: 'Earn EigenLayer points for restaking to secure the AVS network.',
      yieldValue: 'Variable',
      isActive: true,
    },
    {
      title: 'ETH Staking Rewards',
      icon: <WstEthIcon />,
      iconColor: '#00A3FF',
      description: 'Earn ETH staking rewards for securing the Ethereum network.',
      isBase: true,
      isActive: true,
    },
  ],
}

export const rsETHCCopy: YieldTokenCopy = {
  id: YieldTokenId.RSETHC,
  name: 'rsETH Harvest',
  subtitle: 'Grow your rsETH when the price of ETH trends sideways',
  howItWorksDocHref: HELP_COVERED_CALL_STRATEGY_URL,
  risksDocHref: HELP_COVERED_CALL_RISKS_URL,
  pointsLabel: 'Kernel Points (2x)',
  pointsName: 'Points',
  backgroundColor: `${RSETH_COLOR}26`,
  borderColor: RSETH_COLOR,
  assetPath: '/images/yield/rsETHC.png',
  collateralUrl: KELP_URL,
  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy selling out-of-the-money call options on weETH to generate USDC yield. Yields often vary between 10-50% p.a.',
      isOptions: true,
      isActive: true,
    },
    {
      title: 'DRV',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your rsETH into Derive.',
      yieldValue: `${rsETHCConfig}x Multiplier`,
      isActive: true,
    },
    {
      title: 'Kernel Points',
      icon: <KelpIcon />,
      description:
        'Earn boosted Kernel Points for choosing Kelp as your liquid restaking provider.',
      yieldValue: '2x Multiplier',
      isActive: true,
    },
    {
      title: 'EigenLayer Points',
      icon: <EigenLayerIcon />,
      description: 'Earn EigenLayer points for restaking to secure the AVS network.',
      yieldValue: 'Variable',
      isActive: true,
    },
    {
      title: 'ETH Staking Rewards',
      icon: <WstEthIcon />,
      iconColor: '#00A3FF',
      description: 'Earn ETH staking rewards for securing the Ethereum network.',
      isBase: true,
      isActive: true,
    },
  ],
}

export const weETHCSCopy: YieldTokenCopy = {
  id: YieldTokenId.WEETHCS,
  name: 'weETH Safe Harvest',
  subtitle: 'Grow your weETH when the price of ETH trends sideways',
  howItWorksDocHref: HELP_COVERED_CALL_SPREAD_STRATEGY_URL,
  risksDocHref: HELP_COVERED_CALL_SPREAD_RISKS_URL,
  pointsLabel: 'EtherFi Points (4x)',
  pointsName: 'Points',
  backgroundColor: `${WEETH_COLOR}12`,
  borderColor: WEETH_COLOR,
  assetPath: '/images/yield/weETHC.png',
  collateralUrl: ETHERFI_URL,

  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy selling out-of-the-money call options on weETH to generate and compound yield. Yields often vary between 8-30% p.a. The vault also buys call options to limit the downside in case of large upwards price movements.',
      yieldValue: 'Est 8-30%',
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your weETH into Derive.',
      yieldValue: `${weETHCSConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'EtherFi Points',
      icon: <EtherFiIcon />,
      description:
        'Earn boosted EtherFi Points for choosing EtherFi as your liquid restaking provider.',
      yieldValue: '4x Multiplier',
      isActive: true,
    },
    {
      title: 'EigenLayer Points',
      icon: <EigenLayerIcon />,
      description: 'Earn EigenLayer points for restaking to secure the AVS network.',
      yieldValue: 'Variable',
      isActive: true,
    },
    {
      title: 'ETH Staking Rewards',
      icon: <WstEthIcon />,
      iconColor: '#00A3FF',
      description: 'Earn ETH staking rewards for securing the Ethereum network.',
      isBase: true,
      isActive: true,
    },
  ],
}

export const weETHBULLCopy: YieldTokenCopy = {
  id: YieldTokenId.WEETHBULL,
  name: 'weETH Bull',
  subtitle: 'Grow your weETH when the price of ETH goes up',
  howItWorksDocHref: HELP_BULL_STRATEGY_URL,
  risksDocHref: HELP_BULL_RISKS_URL,
  pointsLabel: 'EtherFi Points (4x)',
  pointsName: 'Points',
  backgroundColor: `${WEETH_COLOR}12`,
  borderColor: WEETH_COLOR,
  assetPath: '/images/yield/weETHC.png',
  collateralUrl: ETHERFI_URL,

  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy purchasing call spreads on ETH to generated leveraged long exposure to ETH using your weETH Yield. The strategy can generate up to 2.5x your weETH weekly yield while preserving typical weETH yields during stable markets.',
      yieldValue: 'Est 8-30%',
      isOptions: true,
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your weETH into Derive.',
      yieldValue: `${weETHBULLConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'EtherFi Points',
      icon: <EtherFiIcon />,
      description:
        'Earn boosted EtherFi Points for choosing EtherFi as your liquid restaking provider.',
      yieldValue: '4x Multiplier',
      isActive: true,
    },
    {
      title: 'EigenLayer Points',
      icon: <EigenLayerIcon />,
      description: 'Earn EigenLayer points for restaking to secure the AVS network.',
      yieldValue: 'Variable',
      isActive: true,
    },
  ],
}

export const sUSDeBULLCopy: YieldTokenCopy = {
  id: YieldTokenId.SUSDEBULL,
  name: 'sUSDe Bull',
  subtitle: 'Grow your sUSDe when the price of ETH goes up',
  howItWorksDocHref: HELP_BULL_STRATEGY_URL,
  risksDocHref: HELP_BULL_RISKS_URL,
  pointsLabel: 'Ethena Sats',
  pointsName: 'Sats',
  backgroundColor: '#11111180',
  borderColor: SUSDE_COLOR,
  assetPath: '/images/yield/sUSDe.png',
  collateralUrl: 'https://app.ethena.fi',

  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      isOptions: true,
      description:
        'Automated strategy purchasing call spreads on ETH to generated leveraged long exposure to ETH using your sUSDe Yield. The strategy can generate up to 2.5x your sUSDe weekly yield while preserving typical sUSDe yields during stable markets.',
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      yieldValue: `${sUSDeBULLConfig.pointsMultiplier}x Multiplier`,
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your sUSDe into Derive.',
      isActive: true,
    },
    {
      title: 'Ethena Sats',
      yieldValue: '5x Sats / Day',
      icon: <EnaIcon />,
      description: 'Earn boosted Ethena Sats for holding sUSDe in sUSDe-BULL.',
      isActive: true,
    },
  ],
}

export const LBTCPSCopy: YieldTokenCopy = {
  id: YieldTokenId.LBTCPS,
  name: 'LBTC Maxi',
  subtitle: 'Grow your LBTC when the price of BTC goes up',
  howItWorksDocHref: HELP_COVERED_PUT_SPREAD_STRATEGY_URL,
  risksDocHref: HELP_COVERED_PUT_SPREAD_RISKS_URL,
  pointsLabel: 'Lombard Lux (3x)',
  pointsName: 'Lux',
  backgroundColor: `${LBTC_COLOR}26`,
  borderColor: LBTC_COLOR,
  assetPath: '/images/yield/LBTC.png',
  collateralUrl: LOMBARD_URL,

  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy selling out-of-the-money put options on LBTC to generate and compound yield. Yields often vary between 8-30% p.a. The vault also buys put options to limit the downside in case of large downward price movements.',
      yieldValue: 'Est 8-30%',
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your LBTC into Derive.',
      yieldValue: `${LBTCPSConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'Lombard Lux',
      icon: <LBTC />,
      description:
        'Earn boosted Lombard Lux for choosing Lombard as your liquid restaking provider.',
      yieldValue: '2x Multiplier',
      isActive: true,
    },
    {
      title: 'Babylon Points',
      icon: <BabylonIcon />,
      description: 'Earn Babylon points for restaking to secure the Babylon network.',
      yieldValue: 'Variable',
      isActive: true,
    },
  ],
}

export const LBTCCSCopy: YieldTokenCopy = {
  id: YieldTokenId.LBTCCS,
  name: 'LBTC Safe Harvest',
  subtitle: 'Grow your LBTC when the price of BTC trends sideways',
  howItWorksDocHref: HELP_COVERED_CALL_SPREAD_STRATEGY_URL,
  risksDocHref: HELP_COVERED_CALL_SPREAD_RISKS_URL,
  pointsLabel: 'Lombard Lux (2x)',
  pointsName: 'Points',
  backgroundColor: `${LBTC_COLOR}26`,
  borderColor: LBTC_COLOR,
  assetPath: '/images/yield/LBTC.png',
  collateralUrl: LOMBARD_URL,

  waitingTime: WEEKLY_WAITING_TIME,
  yieldStack: [
    {
      title: 'Options Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated strategy selling out-of-the-money call options on LBTC to generate and compound yield. Yields often vary between 8-30% p.a. The vault also buys call options to limit the downside in case of large upwards price movements.',
      isOptions: true,
      isActive: true,
    },
    {
      title: 'DRV Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV for depositing your LBTC into Derive.',
      yieldValue: `${LBTCCSConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'Lombard Lux',
      icon: <LBTC />,
      description:
        'Earn boosted Lombard Lux for choosing Lombard as your liquid restaking provider.',
      yieldValue: '2x Multiplier',
      isActive: true,
    },
    {
      title: 'Babylon Points',
      icon: <BabylonIcon />,
      description: 'Earn Babylon points for restaking to secure the Babylon network.',
      yieldValue: 'Variable',
      isActive: true,
    },
  ],
}

export const LBTCBCopy: YieldTokenCopy = {
  id: YieldTokenId.bLBTC,
  name: 'Basis Traded LBTC',
  subtitle: 'Earn funding yield from the basis trade while maintaining exposure to LBTC',
  howItWorksDocHref: 'https://help.derive.xyz/en/articles/10732523-delta-1-basis-strategy',
  risksDocHref: 'https://help.derive.xyz/en/articles/10733046-delta-1-basis-risks',
  pointsLabel: 'Lombard Lux (3-6x)',
  pointsName: 'Points',
  backgroundColor: `${LBTC_COLOR}26`,
  borderColor: LBTC_COLOR,
  assetPath: '/images/yield/LBTC.png',
  collateralUrl: LOMBARD_URL,
  waitingTime: {
    label: '0-3 days',
    tooltip:
      'Withdrawals are usually instant, but can take up to 3 days if a large share of the pool is withdrawing.',
  },
  yieldStack: [
    {
      title: 'Basis Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated delta-1 strategy where the vault borrows and buys an amount equal of the underlying asset, effectively doubling the base asset held and hedging this extra amount via short perps. The vault thus collects funding rate payments on the extra amount while keeping exposure to the originally deposited amount.',
      yieldValue: 'Est 5-20%',
      isActive: true,
    },
    {
      title: 'stDRV / OP Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV and OP on trading fees generated by the vault.',
      yieldValue: `${LBTCBConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'Lombard Lux',
      icon: <LBTC />,
      description:
        'Earn boosted Lombard Lux for choosing Lombard as your liquid restaking provider.',
      yieldValue: '3-6x Multiplier',
      isActive: true,
    },
    {
      title: 'Babylon Points',
      icon: <BabylonIcon />,
      description: 'Earn Babylon points for restaking to secure the Babylon network.',
      yieldValue: 'Variable',
      isActive: true,
    },
  ],
}

export const weETHBCopy: YieldTokenCopy = {
  id: YieldTokenId.bweETH,
  name: 'Basis Traded weETH',
  subtitle: 'Earn funding yield from the basis trade while maintaining exposure to weETH',
  howItWorksDocHref: 'https://help.derive.xyz/en/articles/10732523-delta-1-basis-strategy',
  risksDocHref: 'https://help.derive.xyz/en/articles/10733046-delta-1-basis-risks',
  pointsLabel: 'EtherFi Points (4-8x)',
  pointsName: 'Points',
  backgroundColor: `${WEETH_COLOR}12`,
  borderColor: WEETH_COLOR,
  assetPath: '/images/yield/weETHC.png',
  collateralUrl: ETHERFI_URL,
  waitingTime: {
    label: '0-3 days',
    tooltip:
      'Withdrawals are usually instant, but can take up to 3 days if a large share of the pool is withdrawing.',
  },
  yieldStack: [
    {
      title: 'Basis Yield',
      icon: <CubeIcon />,
      iconColor: 'primary',
      description:
        'Automated delta-1 strategy where the vault borrows and buys an amount equal of the underlying asset, effectively doubling the base asset held and hedging this extra amount via short perps. The vault thus collects funding rate payments on the extra amount while keeping exposure to the originally deposited amount.',
      yieldValue: 'Est 5-20%',
      isActive: true,
    },
    {
      title: 'stDRV / OP Rewards',
      icon: <DRVToken />,
      description: 'Earn stDRV and OP on trading fees generated by the vault.',
      yieldValue: `${weETHBConfig.pointsMultiplier}x Multiplier`,
      isActive: true,
    },
    {
      title: 'EtherFi Points (4-8x)',
      icon: <EtherFiIcon />,
      description:
        'Earn boosted EtherFi Points for choosing EtherFi as your liquid restaking provider.',
      yieldValue: '4-8x Multiplier',
      isActive: true,
    },
    {
      title: 'EigenLayer Points',
      icon: <EigenLayerIcon />,
      description: 'Earn EigenLayer points for restaking to secure the AVS network.',
      yieldValue: 'Variable',
      isActive: true,
    },
  ],
}

export const yieldCopy: Record<YieldTokenId, YieldTokenCopy> = {
  [YieldTokenId.RSWETHC]: rswETHCCopy,
  [YieldTokenId.WEETHC]: weETHCCopy,
  [YieldTokenId.RSETHC]: rsETHCCopy,
  [YieldTokenId.SUSDEBULL]: sUSDeBULLCopy,
  [YieldTokenId.WEETHBULL]: weETHBULLCopy,
  [YieldTokenId.WEETHCS]: weETHCSCopy,
  [YieldTokenId.LBTCCS]: LBTCCSCopy,
  [YieldTokenId.LBTCPS]: LBTCPSCopy,
  [YieldTokenId.bLBTC]: LBTCBCopy,
  [YieldTokenId.bweETH]: weETHBCopy,
}
